import React, { useRef, useState, useEffect } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import styled from 'styled-components'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'
import { ChevronDown, Tag } from 'lucide-react'

const Size = Quill.import('attributors/style/size')
Size.whitelist = ['42px', '36px', '28px', '20px', '16px', '12px']
Quill.register(Size, true)

const TAGS = [
    { label: 'Nome do participante', value: '*|PARTICIPANTE|*' },
    { label: 'Nome do evento', value: '*|EVENTO|*' },
    { label: 'Data do evento', value: '*|DATA-DO-EVENTO|*' },
    { label: 'Organizador', value: '*|ORGANIZADOR|*' },
]

const modules = {
    toolbar: {
        container: '#certificate-toolbar',
    },
}

const formats = [
    'size',
    'bold', 'italic', 'underline',
    'align',
    'color',
]

function CertificateEditor({ value, onChange, layoutType, backgroundImage }) {
    const quillRef = useRef(null)
    const [showTags, setShowTags] = useState(false)
    const isCustom = layoutType === 'custom'

    function insertTag(tag) {
        const editor = quillRef.current?.getEditor()
        if (!editor) return
        const range = editor.getSelection(true)
        editor.insertText(range.index, tag)
        editor.setSelection(range.index + tag.length)
        setShowTags(false)
    }

    useEffect(() => {
        function handleClickOutside(e) {
            if (showTags && !e.target.closest('.tags-wrapper')) {
                setShowTags(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showTags])

    return (
        <Container className="certificate-editor">
            {/* Custom toolbar */}
            <ToolbarCard id="certificate-toolbar">
                <ToolbarRow>
                    <button className="ql-bold" />
                    <button className="ql-italic" />
                    <button className="ql-underline" />
                    <ToolbarDivider />
                    <button className="ql-align" value="" />
                    <button className="ql-align" value="center" />
                    <button className="ql-align" value="right" />
                    <ToolbarDivider />
                    <select className="ql-color" />
                </ToolbarRow>

                <DropdownsRow>
                    <select className="ql-size">
                        <option value="42px">Titulo Grande</option>
                        <option value="36px">Titulo</option>
                        <option value="28px">Subtitulo</option>
                        <option value="20px">Normal</option>
                        <option value="16px">Pequeno</option>
                        <option value="12px">Muito Pequeno</option>
                    </select>

                    <TagsWrapper className="tags-wrapper">
                        <TagsButton type="button" onClick={() => setShowTags(!showTags)}>
                            <Tag size={13} />
                            TAGS
                            <ChevronDown
                                size={12}
                                style={{
                                    transition: 'transform .2s ease',
                                    transform: showTags ? 'rotate(180deg)' : 'rotate(0)',
                                }}
                            />
                        </TagsButton>

                        {showTags && (
                            <TagsDropdown>
                                <DropdownTitle>Selecione uma tag</DropdownTitle>
                                {TAGS.map(tag => (
                                    <TagItem key={tag.value} onClick={() => insertTag(tag.value)}>
                                        <TagName>{tag.label}</TagName>
                                        <TagCode>{tag.value}</TagCode>
                                    </TagItem>
                                ))}
                            </TagsDropdown>
                        )}
                    </TagsWrapper>
                </DropdownsRow>
            </ToolbarCard>

            {/* Unified editor + preview */}
            <CertificateCanvas isCustom={isCustom} backgroundImage={backgroundImage}>
                {!isCustom && <CertificateBorder />}
                <EditorOverlay>
                    <ReactQuill
                        ref={quillRef}
                        value={value}
                        onChange={onChange}
                        theme="snow"
                        modules={modules}
                        formats={formats}
                    />
                </EditorOverlay>
            </CertificateCanvas>
        </Container>
    )
}

export default CertificateEditor

/* ---------- Styled Components ---------- */

const Container = styled.div`
    width: 100%;
`

const ToolbarCard = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px 10px 0 0;
    padding: 0;

    /* Reset all quill toolbar defaults */
    &.ql-toolbar.ql-snow {
        border: none;
        padding: 0;
    }

    .ql-formats {
        margin-right: 0 !important;
        display: contents;
    }

    button {
        width: 34px !important;
        height: 34px !important;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        border: 1px solid ${colors.neutralDark} !important;
        border-radius: 6px;
        background: ${colors.neutralLight};
        cursor: pointer;
        transition: all .15s ease;
        padding: 0;

        &:hover, &.ql-active {
            background: #F3EAFF;
            border-color: ${colors.occasionalPurple}40 !important;
        }

        &.ql-active svg {
            stroke: ${colors.occasionalPurple};
        }
    }

    .ql-picker.ql-color {
        width: 34px !important;
        height: 34px !important;

        .ql-picker-label {
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid ${colors.neutralDark};
            border-radius: 6px;
            padding: 0;
            width: 34px;
            height: 34px;
            transition: all .15s ease;

            &:hover {
                background: #F3EAFF;
                border-color: ${colors.occasionalPurple}40;
            }

            svg {
                width: 16px;
                height: 16px;
            }
        }
    }

    .ql-picker.ql-size {
        flex: 1;
        height: 36px;

        .ql-picker-label {
            display: flex;
            align-items: center;
            border: 1px solid ${colors.neutralDark};
            border-radius: 8px;
            padding: 0 12px;
            height: 36px;
            font-size: ${fonts.sizes.small};
            color: ${colors.nightDark};
            background: ${colors.neutralLight};
            transition: all .15s ease;

            &:hover {
                border-color: ${colors.occasionalPurple}40;
            }

            &::before {
                font-size: ${fonts.sizes.small} !important;
            }
        }

        .ql-picker-options {
            border-radius: 8px;
            border: 1px solid ${colors.neutralDark};
            box-shadow: 0 4px 16px rgba(0,0,0,0.08);
            padding: 4px;
        }

        .ql-picker-item {
            padding: 6px 10px;
            border-radius: 6px;
            font-size: ${fonts.sizes.small} !important;

            &:hover {
                background: #F3EAFF;
            }

            &::before {
                font-size: ${fonts.sizes.small} !important;
            }
        }
    }

    .ql-snow .ql-picker.ql-size .ql-picker-label::before {
        content: 'Tamanho';
    }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="42px"]::before { content: 'Titulo Grande'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: 'Titulo'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: 'Subtitulo'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: 'Normal'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: 'Pequeno'; }
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: 'Muito Pequeno'; }
`

const ToolbarRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-wrap: wrap;
`

const ToolbarDivider = styled.div`
    width: 1px;
    height: 20px;
    background: ${colors.neutralDark};
    margin: 0 4px;
`

const DropdownsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;

    .ql-formats {
        flex: 1;
        display: flex !important;
    }
`

/* ---------- Tags ---------- */

const TagsWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`

const TagsButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.occasionalPurple};
    cursor: pointer;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    height: 36px;
    transition: all .2s ease;
    white-space: nowrap;

    &:hover {
        background: #F3EAFF;
        border-color: ${colors.occasionalPurple}40;
    }
`

const TagsDropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 50;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 0;
    min-width: 340px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    overflow: hidden;
`

const DropdownTitle = styled.div`
    padding: 10px 14px;
    font-size: ${fonts.sizes.small};
    font-weight: 700;
    color: ${colors.night};
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
`

const TagItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    cursor: pointer;
    gap: 14px;
    transition: background .15s ease;

    &:hover {
        background: ${colors.neutralBackground};
    }
`

const TagName = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightDark};
    font-weight: 600;
`

const TagCode = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    color: ${colors.occasionalPurple};
    background: #F3EAFF;
    padding: 3px 8px;
    border-radius: 6px;
    white-space: nowrap;
`

/* ---------- Unified certificate canvas ---------- */

const CertificateCanvas = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 1.414 / 1;
    border-radius: 0 0 10px 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ isCustom, backgroundImage }) =>
        isCustom && backgroundImage
            ? `url(${backgroundImage}) center/cover no-repeat`
            : '#FDFBF7'
    };
    border: 1px solid ${colors.neutralDark};
    border-top: none;
`

const CertificateBorder = styled.div`
    position: absolute;
    inset: 10px;
    border: 3px solid #C9A84C;
    pointer-events: none;
    z-index: 1;

    &::after {
        content: '';
        position: absolute;
        inset: 7px;
        border: 1px solid #C9A84C;
        opacity: 0.6;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        inset: 14px;
        &::after { inset: 7px; }
    }
`

const EditorOverlay = styled.div`
    position: relative;
    z-index: 2;
    width: 78%;
    max-height: 82%;
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    .quill {
        width: 100%;
    }

    .ql-toolbar.ql-snow {
        display: none !important;
    }

    .ql-container.ql-snow {
        border: none;
        font-size: ${fonts.sizes.default};
    }

    .ql-editor {
        padding: 16px 8px;
        text-align: center;
        min-height: auto;
        background: transparent;

        p {
            margin: 4px 0;
            font-size: 11px;
            line-height: 1.5;
        }

        h1, h2, h3 { margin: 6px 0; }
        strong { font-weight: bold; }
    }

    .ql-editor.ql-blank::before {
        text-align: center;
        font-style: normal;
        color: ${colors.night};
        opacity: 0.5;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        .ql-editor p { font-size: 12px; }
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        .ql-editor {
            padding: 24px 16px;
            p { font-size: 13px; }
        }
    }
`
